/**
 * Registry of emulated systems.
 *
 * A system is client-independent. Each client declares which *engines* it can
 * run (the web client runs EmulatorJS cores; a future native client could run
 * native RetroArch cores, DuckStation, melonDS…). Saves are tagged with the
 * engine + core + version that produced them, so incompatible states are
 * never cross-loaded.
 */
export type SupportLevel = 'verified' | 'partial' | 'experimental' | 'unsupported';

export interface EngineProfile {
  /** Stable engine id, e.g. "emulatorjs". */
  engine: string;
  /** Core identifier inside the engine. */
  core: string;
  /** Whether this engine can run in browsers. */
  web: boolean;
  /** Needs SharedArrayBuffer (cross-origin isolation). */
  threads?: boolean;
  /** Support level verified by REPLAY tests (not merely claimed by upstream). */
  support: SupportLevel;
  notes?: string;
}

export interface SystemDef {
  id: string;
  name: string;
  shortName: string;
  manufacturer: string;
  year: number;
  /** Lowercase file extensions (without dot) accepted on import. */
  extensions: string[];
  /** Typical max local players. */
  maxPlayers: number;
  /** REPLAY's patched core supports online input sync. */
  netplay: boolean;
  /** Supports consoles connected by a link cable. */
  link?: boolean;
  /** How many consoles its cable joins (2 when unset). */
  linkPlayers?: number;
  /** Dual-screen or other layout specifics. */
  dualScreen?: boolean;
  /** BIOS files the admin may provide (never bundled). */
  bios?: { file: string; required: boolean; description: string }[];
  engines: EngineProfile[];
  /** Header-based detection for ambiguous extensions. */
  accent: string;
}

export const SYSTEMS: SystemDef[] = [
  {
    id: 'gb', name: 'Game Boy', shortName: 'GB', manufacturer: 'Nintendo', year: 1989,
    extensions: ['gb'], maxPlayers: 1, netplay: false, link: true, accent: '#8bac0f',
    engines: [{ engine: 'emulatorjs', core: 'gambatte', web: true, support: 'verified' }],
  },
  {
    id: 'gbc', name: 'Game Boy Color', shortName: 'GBC', manufacturer: 'Nintendo', year: 1998,
    extensions: ['gbc'], maxPlayers: 1, netplay: false, link: true, accent: '#6a3fb5',
    engines: [{ engine: 'emulatorjs', core: 'gambatte', web: true, support: 'partial' }],
  },
  {
    id: 'gba', name: 'Game Boy Advance', shortName: 'GBA', manufacturer: 'Nintendo', year: 2001,
    extensions: ['gba'], maxPlayers: 1, netplay: false, link: true, linkPlayers: 4, accent: '#5b4fd6',
    bios: [{ file: 'gba_bios.bin', required: false, description: 'BIOS oficial (opcional, mejora compatibilidad)' }],
    engines: [{ engine: 'emulatorjs', core: 'mgba', web: true, support: 'verified' }],
  },
  {
    id: 'nes', name: 'Nintendo Entertainment System', shortName: 'NES', manufacturer: 'Nintendo', year: 1985,
    extensions: ['nes', 'fds', 'unf', 'unif'], maxPlayers: 2, netplay: true, accent: '#d03a3a',
    engines: [{ engine: 'emulatorjs', core: 'fceumm', web: true, support: 'partial' }],
  },
  {
    id: 'snes', name: 'Super Nintendo', shortName: 'SNES', manufacturer: 'Nintendo', year: 1990,
    extensions: ['sfc', 'smc'], maxPlayers: 2, netplay: true, accent: '#7d6bd1',
    engines: [{ engine: 'emulatorjs', core: 'snes9x', web: true, support: 'partial' }],
  },
  {
    id: 'segaMD', name: 'Mega Drive', shortName: 'MD', manufacturer: 'Sega', year: 1988,
    extensions: ['md', 'gen', 'smd', 'bin'], maxPlayers: 2, netplay: true, accent: '#2f6fe0',
    engines: [{ engine: 'emulatorjs', core: 'genesis_plus_gx', web: true, support: 'experimental' }],
  },
  {
    id: 'n64', name: 'Nintendo 64', shortName: 'N64', manufacturer: 'Nintendo', year: 1996,
    extensions: ['n64', 'z64', 'v64'], maxPlayers: 4, netplay: true, accent: '#1fa34a',
    engines: [{ engine: 'emulatorjs', core: 'mupen64plus_next', web: true, support: 'partial', notes: 'Exigente: requiere WebGL2 y un dispositivo de gama media o superior.' }],
  },
  {
    id: 'nds', name: 'Nintendo DS', shortName: 'NDS', manufacturer: 'Nintendo', year: 2004,
    extensions: ['nds'], maxPlayers: 1, netplay: false, dualScreen: true, accent: '#9aa3ad',
    engines: [{ engine: 'emulatorjs', core: 'melonds', web: true, support: 'experimental', notes: 'Rendimiento dependiente del dispositivo.' }],
  },
  {
    id: 'psx', name: 'PlayStation', shortName: 'PS1', manufacturer: 'Sony', year: 1994,
    extensions: ['chd', 'pbp', 'cue', 'iso', 'img', 'zip'], maxPlayers: 2, netplay: false, accent: '#c9ccd6',
    bios: [{ file: 'scph5501.bin', required: false, description: 'BIOS PS1 (opcional; sin ella se usa BIOS HLE)' }],
    engines: [{ engine: 'emulatorjs', core: 'pcsx_rearmed', web: true, support: 'experimental', notes: 'Formato recomendado: CHD o PBP (un único archivo).' }],
  },
  {
    id: 'dos', name: 'MS-DOS', shortName: 'DOS', manufacturer: 'PC', year: 1981,
    extensions: ['zip', 'jsdos'], maxPlayers: 1, netplay: false, accent: '#b08a3e',
    engines: [{ engine: 'emulatorjs', core: 'dosbox_pure', web: true, support: 'experimental' }],
  },
];

export const SYSTEMS_BY_ID: Record<string, SystemDef> = Object.fromEntries(SYSTEMS.map((s) => [s.id, s]));

/** Extensions shared by several systems need an explicit choice by the admin. */
export function systemsForExtension(ext: string): SystemDef[] {
  const e = ext.toLowerCase().replace(/^\./, '');
  return SYSTEMS.filter((s) => s.extensions.includes(e));
}

export function webEngine(systemId: string): EngineProfile | undefined {
  return SYSTEMS_BY_ID[systemId]?.engines.find((e) => e.web);
}
